// The i18next singleton, NOT './config'. Importing the config module runs
// `.use(initReactI18next)` as a side effect at load time, which breaks the 35
// suites that mock react-i18next (the lesson from #1143). These helpers are
// imported by ~30 components, so that side effect would be everywhere.
// `./config` is loaded once at the app root by I18nProvider, which is what
// populates the singleton this module reads.
import i18n from 'i18next';

/**
 * Date/time formatting bound to the language the user selected in the app.
 *
 * Passing `[]` (or nothing) as the locale argument to `toLocaleString` &co.
 * resolves to the *browser's* locale, which desynchronizes from the app's
 * language toggle in both directions — and on a dock kiosk the browser locale
 * is whatever the machine was imaged with, something the operator cannot
 * change. Always format through these helpers instead.
 *
 * `i18n.language` is read at call time, not at import time, so a language
 * switch takes effect on the next render (components consuming `useTranslation`
 * re-render on `languageChanged`).
 *
 * NOTE: `utils/time.js` also exports a `formatDateTime`, with a different
 * fallback ('N/A') and a different shape. It now delegates here for the
 * formatting itself; see the comment there before adding a third one.
 */
const locale = () => i18n.language || 'en';

/** Short date + short time, e.g. "24/08/2026 19:52". `—` for missing/invalid. */
export function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(locale(), { dateStyle: 'short', timeStyle: 'short' });
}

/** Time to the second, e.g. "19:52:07". Empty string for missing/invalid. */
export function formatTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** Wall clock to the minute, e.g. "19:52". Empty string for missing/invalid. */
export function formatClock(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
}

/**
 * Date + time exactly as the runtime's locale writes it, e.g.
 * "24/08/2026, 19:52:07" in pt-BR and "8/24/2026, 7:52:07 PM" in en.
 *
 * Deliberately passes NO date/time style: the ~20 call sites this replaced
 * used a bare `toLocaleString()`, whose output includes seconds and a 4-digit
 * year. `formatDateTime` above (dateStyle/timeStyle 'short') drops both, so
 * using it here would have quietly degraded audit logs, telemetry and event
 * tables — where the seconds are the reason the column exists — under the
 * cover of an i18n fix. Pass `options` when a screen wants a specific shape.
 */
export function formatTimestamp(value, options) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(locale(), options);
}

/** Date without time, in the app's language. `—` for missing/invalid. */
export function formatDate(value, options) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(locale(), options);
}

/**
 * Percentage from a 0..1 ratio, in the app's language.
 *
 * The locale matters for more than digits here: pt-BR writes 12,5% where
 * en-US writes 12.5%, so a browser-locale format shows the wrong separator
 * next to copy in the other language.
 */
export function formatPercent(value, { minimumFractionDigits = 1, maximumFractionDigits = 2 } = {}) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0%';
  return `${(numeric * 100).toLocaleString(locale(), { minimumFractionDigits, maximumFractionDigits })}%`;
}

/** Plain number in the app's language (thousands/decimal separators). */
export function formatNumber(value, options = {}) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  return numeric.toLocaleString(locale(), options);
}
